import type { ImageTriageFinding, ImageTriageState } from "../../types";

export const OBSERVE_ONLY_COPY =
  "These findings are for operator review. No uploads were removed, reordered, hidden, or prevented from reaching the director.";
export const ENFORCEMENT_COPY =
  "Strict identity enforcement keeps one upload per byte-identical or canonical-pixel-identical group. Altered and similar variants remain advisory.";

export const IMAGE_TRIAGE_STATUS_COPY: Record<
  ImageTriageState,
  { badge: string; description: string }
> = {
  complete: {
    badge: "Complete",
    description: "Image preprocessing analysis is complete.",
  },
  partial_analyzer: {
    badge: "Partial",
    description:
      "Some images or checks could not be analyzed. Available observations are shown below.",
  },
  partial_projection: {
    badge: "Processing",
    description:
      "Some report rows have not reached the CRM yet. Available observations are shown below.",
  },
  pending: {
    badge: "Processing",
    description: "Image preprocessing results are still arriving.",
  },
  no_images: {
    badge: "Not run · no images",
    description: "This Echo did not contain images to analyze.",
  },
  unavailable: {
    badge: "Unavailable",
    description: "Image preprocessing is unavailable for this Echo.",
  },
};

type FindingCopy = {
  badge: string;
  tone: string;
  tooltip: (finding: ImageTriageFinding) => string;
};

const numberEvidence = (finding: ImageTriageFinding, key: string) => {
  const value = finding.evidence[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

// Operator copy written by the vision model. Clamped again here rather than
// trusted: this string is rendered, and the backend limit is a promise, not a
// guarantee this surface should depend on.
const VLM_REASON_MAX_CHARS = 160;

const vlmReason = (finding: ImageTriageFinding): string | null => {
  const value = finding.evidence.vlm_reason;
  if (typeof value !== "string") return null;
  const trimmed = value.trim().slice(0, VLM_REASON_MAX_CHARS);
  if (!trimmed) return null;
  // Sentence-case it so it reads naturally at the head of the tooltip.
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const friendlyReason = (reason?: string | null) => {
  const copy: Record<string, string> = {
    analysis_failed: "the analyzer could not complete",
    decode_failed: "the image could not be decoded",
    download_failed: "the image could not be read",
    missing_object: "the source object was unavailable",
    unsupported_format: "the image format was unsupported",
    byte_budget_exceeded:
      "the encoded file exceeded the signed analysis budget",
    decoded_megapixel_budget_exceeded:
      "the decoded image exceeded the signed analysis budget",
    detector_timeout: "a configured check timed out",
    provider_unavailable: "a configured analyzer was unavailable",
    detector_skipped_budget: "a configured check reached its signed budget",
    // Emitted by the Tier-2 vision lane.
    detector_timed_out: "the vision check timed out",
    detector_failed: "the vision provider returned an error",
    provider_schema_mismatch:
      "the vision provider returned an unreadable answer",
    budget_exhausted: "this Echo reached its signed vision-check budget",
    // The model answered "I cannot tell" — a correct abstention (no identifiable
    // subject, too dark), not a fault to investigate.
    metric_unavailable:
      "the image had no clearly identifiable subject to judge",
  };
  return copy[reason ?? ""] ?? "the analyzer could not complete";
};

export const IMAGE_TRIAGE_FINDING_COPY: Record<string, FindingCopy> = {
  exact_duplicate: {
    badge: "Exact duplicate",
    tone: "border-slate-300 bg-slate-50 text-slate-800 dark:bg-slate-950/40 dark:text-slate-200",
    tooltip: () =>
      "Same source bytes as another upload in this Echo. This is a strict identity match.",
  },
  same_pixels: {
    badge: "Same photo",
    tone: "border-slate-300 bg-slate-50 text-slate-800 dark:bg-slate-950/40 dark:text-slate-200",
    tooltip: () =>
      "The upright decoded pixels match another upload, even though the files may differ.",
  },
  altered_copy: {
    badge: "Altered/annotated variant",
    tone: "border-violet-300 bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-200",
    tooltip: () =>
      "Looks visually related after re-encoding, resizing, cropping, or localized annotation. This is advisory and is not a strict duplicate classification.",
  },
  similar_moment: {
    badge: "Similar moment",
    tone: "border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-200",
    tooltip: () =>
      "Looks like another shot from the same moment. Grouped for review; not treated as a duplicate.",
  },
  resolution_critical: {
    badge: "Very low resolution",
    tone: "border-red-400 bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200",
    tooltip: (finding) => {
      const shortEdge = numberEvidence(finding, "short_edge");
      const threshold = numberEvidence(finding, "threshold_short_edge");
      const upscale = numberEvidence(finding, "expected_upscale");
      const facts = [
        shortEdge == null ? null : `Short edge: ${shortEdge}px`,
        threshold == null ? null : `critical boundary: ${threshold}px`,
        upscale == null ? null : `expected enlargement: ${upscale.toFixed(1)}×`,
      ].filter(Boolean);
      return `${facts.length ? `${facts.join("; ")}. ` : ""}This did not block the Echo.`;
    },
  },
  resolution_watch: {
    badge: "Low resolution",
    tone: "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
    tooltip: (finding) => {
      const width = numberEvidence(finding, "width");
      const height = numberEvidence(finding, "height");
      const megapixels = numberEvidence(finding, "megapixels");
      const dimensions =
        width != null && height != null ? `${width}×${height}` : "Dimensions";
      return `${dimensions}${megapixels == null ? "" : `, ${megapixels.toFixed(2)}MP`} are below the review profile.`;
    },
  },
  expected_upscale_watch: {
    badge: "Upscale review",
    tone: "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
    tooltip: (finding) => {
      const upscale = numberEvidence(finding, "expected_upscale");
      return `The configured delivery target may require ${upscale == null ? "significant" : `approximately ${upscale.toFixed(1)}×`} enlargement after crop.`;
    },
  },
  focus_likely_blur: {
    badge: "Likely blurry",
    tone: "border-orange-400 bg-orange-50 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200",
    tooltip: () =>
      "Multiple focus measurements were below the calibrated range. This is a review flag, not an automatic rejection.",
  },
  focus_watch: {
    badge: "Focus review",
    tone: "border-amber-300 bg-transparent text-amber-900 dark:text-amber-200",
    tooltip: () =>
      "Some focus measurements were low or inconsistent. Check the full image before deciding.",
  },
  focus_unassessable: {
    badge: "Focus unknown",
    tone: "border-muted-foreground/40 bg-transparent text-muted-foreground",
    tooltip: () =>
      "This image did not contain enough reliable natural-image detail for an automatic focus result.",
  },
  possible_screenshot: {
    badge: "Possible screenshot",
    tone: "border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200",
    tooltip: () =>
      "The image-analysis signal suggests this may be a screenshot or image inside a device frame.",
  },
  synthetic_border: {
    badge: "Border/composite",
    tone: "border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200",
    tooltip: () =>
      "A border or non-photographic frame may surround the main image content.",
  },
  analysis_failed: {
    badge: "Not analyzed",
    tone: "border-destructive bg-transparent text-destructive",
    tooltip: (finding) =>
      `Image preprocessing could not analyze this file: ${friendlyReason(String(finding.evidence.reason_code ?? ""))}. The file still reached the director.`,
  },
  detector_skipped_budget: {
    badge: "Check not run",
    tone: "border-muted-foreground/40 bg-transparent text-muted-foreground",
    tooltip: () =>
      "A configured check reached its signed analysis budget. The image still reached the director.",
  },
  // --- Tier 2: vision checks on the focus gray zone (MQG-010) ---------------
  // `evidence.vlm_reason` is written by the vision model for exactly this
  // surface: short, plain, and about the defect rather than the people in the
  // photo. Show it verbatim when present and fall back to the general
  // explanation when it is not.
  subject_motion_blur: {
    badge: "Subject in motion",
    tone: "border-orange-400 bg-orange-50 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200",
    tooltip: (finding) =>
      `${vlmReason(finding) ?? "The subject looks smeared by movement"}. The background can still be sharp when the subject moved. This is a review flag, not an automatic rejection.`,
  },
  subject_out_of_focus: {
    badge: "Subject out of focus",
    tone: "border-orange-400 bg-orange-50 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200",
    tooltip: (finding) =>
      `${vlmReason(finding) ?? "The subject looks soft, as if the camera focused elsewhere"}. A deliberately blurred background is not counted. This is a review flag, not an automatic rejection.`,
  },
  vlm_quality_ok: {
    badge: "Looks sharp",
    tone: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200",
    tooltip: (finding) =>
      `${vlmReason(finding) ?? "A vision check reviewed this image and found the subject acceptably sharp"}. Shown because an automatic focus measurement was inconclusive.`,
  },
  vlm_quality_unavailable: {
    badge: "Sharpness unknown",
    tone: "border-muted-foreground/40 bg-transparent text-muted-foreground",
    tooltip: (finding) =>
      `The vision check could not give a verdict for this image: ${friendlyReason(String(finding.evidence.reason_code ?? ""))}. The file still reached the director.`,
  },
  // --- Tier 2: similarity adjudication on grouping gray zones (SIM-004) -----
  // A vision model reviewed a borderline group (or a set the classical
  // detector rejected) and said whether the photos capture one moment. All
  // advisory: groups are never created or dissolved by these verdicts.
  similar_adjudication_confirmed: {
    badge: "AI: same moment",
    tone: "border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-200",
    tooltip: (finding) =>
      `${vlmReason(finding) ?? "A vision review confirmed these photos capture the same moment"}. The group was borderline on classical evidence, so a vision model reviewed it.`,
  },
  similar_adjudication_rejected: {
    badge: "AI: different moments",
    tone: "border-slate-300 bg-slate-50 text-slate-800 dark:bg-slate-950/40 dark:text-slate-200",
    tooltip: (finding) =>
      `${vlmReason(finding) ?? "A vision review judged these photos to be from different moments"}. The classical grouping stays visible; treat it with less confidence.`,
  },
  similar_adjudication_proposed: {
    badge: "AI proposed: same moment",
    tone: "border-indigo-300 bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-200",
    tooltip: (finding) =>
      `${vlmReason(finding) ?? "A vision review believes these photos capture the same moment"}. The classical detector did not group them — this is an AI proposal for review.`,
  },
  similar_adjudication_unavailable: {
    badge: "Moment review unavailable",
    tone: "border-muted-foreground/40 bg-transparent text-muted-foreground",
    tooltip: (finding) =>
      `The vision moment review could not give a verdict: ${friendlyReason(String(finding.evidence.reason_code ?? ""))}. The classical grouping is unaffected.`,
  },
};

export const findingCopy = (finding: ImageTriageFinding): FindingCopy =>
  IMAGE_TRIAGE_FINDING_COPY[finding.code] ?? {
    badge: finding.label || "Image finding",
    tone: "border-muted-foreground/40 bg-transparent text-muted-foreground",
    tooltip: () =>
      "A newer image-preprocessing finding was recorded. Review the full image and technical details.",
  };

export const findingTooltip = (finding: ImageTriageFinding) =>
  findingCopy(finding).tooltip(finding);

const FINDING_PRIORITY = [
  "analysis_failed",
  "detector_skipped_budget",
  "resolution_critical",
  // A vision verdict outranks the classical focus band it was called to settle:
  // when both are present the vision answer is the more specific one.
  "subject_motion_blur",
  "subject_out_of_focus",
  "focus_likely_blur",
  "exact_duplicate",
  "same_pixels",
  "altered_copy",
  "similar_moment",
  "similar_adjudication_confirmed",
  "similar_adjudication_proposed",
  "similar_adjudication_rejected",
  "resolution_watch",
  "expected_upscale_watch",
  "focus_watch",
  "focus_unassessable",
  "possible_screenshot",
  "synthetic_border",
  // Informational tail: a positive verdict and a missing verdict are context
  // for the flags above, never the headline on a thumbnail.
  "vlm_quality_ok",
  "vlm_quality_unavailable",
  "similar_adjudication_unavailable",
];

/**
 * Every finding code the orchestrator can emit today.
 *
 * Intentional tripwire: the copy-completeness test fails when the orchestrator
 * adds a code and the CRM has no copy for it, so a new finding surfaces as a
 * deliberate copy decision instead of a humanized enum leaking into the UI.
 */
export const BACKEND_FINDING_CODES = [
  "exact_duplicate",
  "same_pixels",
  "altered_copy",
  "similar_moment",
  "resolution_critical",
  "resolution_watch",
  "expected_upscale_watch",
  "focus_likely_blur",
  "focus_watch",
  "focus_unassessable",
  "possible_screenshot",
  "synthetic_border",
  "analysis_failed",
  "detector_skipped_budget",
  "subject_motion_blur",
  "subject_out_of_focus",
  "vlm_quality_ok",
  "vlm_quality_unavailable",
  "similar_adjudication_confirmed",
  "similar_adjudication_rejected",
  "similar_adjudication_proposed",
  "similar_adjudication_unavailable",
] as const;

export const rankFindings = (findings: ImageTriageFinding[]) =>
  [...findings].sort((left, right) => {
    const leftRank = FINDING_PRIORITY.indexOf(left.code);
    const rightRank = FINDING_PRIORITY.indexOf(right.code);
    return (
      (leftRank < 0 ? FINDING_PRIORITY.length : leftRank) -
        (rightRank < 0 ? FINDING_PRIORITY.length : rightRank) ||
      left.code.localeCompare(right.code)
    );
  });
